import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import { AuthContext, API } from '@/App';
import { toast } from 'sonner';
import {
  Upload, Loader2, X, FileText, CheckCircle2, AlertTriangle, ShieldAlert,
  PenLine, Clock, RefreshCw, ChevronLeft, ChevronRight, FileWarning, ZoomIn, ZoomOut,
} from 'lucide-react';

pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const auth = (t) => ({ headers: { Authorization: `Bearer ${t}` } });

const PdfViewer = ({ fileUrl, page, setPage }) => {
  const [numPages, setNumPages] = useState(0);
  const [scale, setScale] = useState(1);
  const [width, setWidth] = useState(560);
  const wrapRef = useRef(null);
  useEffect(() => {
    const el = wrapRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(() => setWidth(Math.max(240, el.clientWidth - 24)));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const options = useMemo(() => ({}), []);
  const safePage = numPages ? Math.min(page, numPages) : page;
  return (
    <div className="flex h-full flex-col" data-testid="intake-pdf-viewer">
      <div className="flex items-center justify-between gap-2 border-b border-slate-200 bg-white px-2 py-1.5 text-xs">
        <div className="flex items-center gap-1">
          <button onClick={() => setPage(Math.max(1, safePage - 1))} disabled={safePage <= 1} data-testid="pdf-prev"
            className="rounded p-1 text-slate-600 hover:bg-slate-100 disabled:opacity-40"><ChevronLeft size={16} /></button>
          <span className="tabular-nums text-slate-600" data-testid="pdf-page-indicator">Page {safePage} / {numPages || '…'}</span>
          <button onClick={() => setPage(Math.min(numPages || safePage, safePage + 1))} disabled={!!numPages && safePage >= numPages} data-testid="pdf-next"
            className="rounded p-1 text-slate-600 hover:bg-slate-100 disabled:opacity-40"><ChevronRight size={16} /></button>
        </div>
        <div className="flex items-center gap-1">
          <button onClick={() => setScale((s) => Math.max(0.5, +(s - 0.25).toFixed(2)))} data-testid="pdf-zoom-out"
            className="rounded p-1 text-slate-600 hover:bg-slate-100"><ZoomOut size={16} /></button>
          <span className="tabular-nums text-slate-500">{Math.round(scale * 100)}%</span>
          <button onClick={() => setScale((s) => Math.min(3, +(s + 0.25).toFixed(2)))} data-testid="pdf-zoom-in"
            className="rounded p-1 text-slate-600 hover:bg-slate-100"><ZoomIn size={16} /></button>
        </div>
      </div>
      <div ref={wrapRef} className="flex-1 overflow-auto bg-slate-300 p-3" data-testid="intake-pdf-frame">
        {fileUrl ? (
          <Document file={fileUrl} options={options} onLoadSuccess={({ numPages: n }) => setNumPages(n)}
            loading={<div className="flex justify-center py-10 text-slate-500"><Loader2 className="animate-spin" /></div>}
            error={<div className="py-10 text-center text-sm text-rose-600">Could not load the PDF. Try reopening.</div>}>
            <Page pageNumber={safePage} width={width * scale} renderTextLayer={false} renderAnnotationLayer={false}
              className="mx-auto shadow-lg" loading={<div className="flex justify-center py-10 text-slate-400"><Loader2 className="animate-spin" /></div>} />
          </Document>
        ) : (
          <div className="flex h-full items-center justify-center text-slate-400"><Loader2 size={22} className="animate-spin" /></div>
        )}
      </div>
    </div>
  );
};

const STATUS_META = {
  conflict: { label: 'Conflict', tone: 'text-rose-700 bg-rose-50 border-rose-200', dot: 'bg-rose-500' },
  needs_verification: { label: 'Needs Verification', tone: 'text-amber-700 bg-amber-50 border-amber-200', dot: 'bg-amber-500' },
  new: { label: 'New Information', tone: 'text-blue-700 bg-blue-50 border-blue-200', dot: 'bg-blue-500' },
  matched: { label: 'Matched', tone: 'text-emerald-700 bg-emerald-50 border-emerald-200', dot: 'bg-emerald-500' },
  historical: { label: 'Historical', tone: 'text-slate-600 bg-slate-100 border-slate-200', dot: 'bg-slate-400' },
};
const ORDER = ['conflict', 'needs_verification', 'new', 'matched', 'historical'];
const CAT_LABEL = {
  identity: 'Identity', contact: 'Contact', authorization: 'Identification & Work Authorization',
  employment: 'Employment', tax: 'Tax (historical)', property: 'Property & Keys (historical)',
  acknowledgment: 'Signed Acknowledgments (historical)', historical: 'Historical',
};

const FieldRow = ({ f, sid, token, onChange, onJump, reportSave }) => {
  const [val, setVal] = useState(f.confirmed_value ?? f.draft_value ?? f.display_value ?? f.extracted_value);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const timer = useRef(null);
  const meta = STATUS_META[f.match_status] || STATUS_META.new;
  const saveDraft = async (nextVal) => {
    reportSave?.('saving');
    try {
      await axios.patch(`${API}/personnel-intake/${sid}/field/${f.key}`, { draft_value: nextVal }, auth(token));
      onChange(f.key, { draft_value: nextVal });
      setSaved(true); setTimeout(() => setSaved(false), 1500);
      reportSave?.('saved');
    } catch (_) { reportSave?.('error'); }
  };
  const onInput = (e) => {
    const v = e.target.value; setVal(v);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => saveDraft(v), 600);
  };
  const flush = () => { clearTimeout(timer.current); saveDraft(val); };
  const toggle = async (confirmed) => {
    clearTimeout(timer.current);
    setBusy(true); reportSave?.('saving');
    try {
      await axios.patch(`${API}/personnel-intake/${sid}/field/${f.key}`, { confirmed, draft_value: val }, auth(token));
      onChange(f.key, { confirmed, confirmed_value: confirmed ? val : null, draft_value: val });
      reportSave?.('saved');
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not update field'); reportSave?.('error'); }
    setBusy(false);
  };
  return (
    <div className={`rounded-lg border p-3 ${f.confirmed ? 'border-emerald-300 bg-emerald-50/40' : 'border-slate-200 bg-white'}`} data-testid={`intake-field-${f.key}`}>
      <div className="mb-1 flex items-center justify-between gap-2">
        <span className="text-xs font-bold text-[#0E2A47]">{f.label}</span>
        <span className={`rounded-full border px-2 py-0.5 text-[10px] font-semibold ${meta.tone}`}>{meta.label}</span>
      </div>
      <input
        value={val} onChange={onInput} onBlur={flush}
        disabled={f.confirmed}
        className="w-full rounded border border-slate-300 px-2 py-1 text-sm text-slate-800 disabled:bg-slate-50 disabled:text-slate-500"
        data-testid={`intake-field-input-${f.key}`}
      />
      {f.existing_value != null && (
        <div className="mt-1 text-[11px] text-slate-500">Current record: <span className="font-medium text-slate-600">{f.existing_display ?? f.existing_value}</span></div>
      )}
      <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-[10px] text-slate-400">
        <button type="button" onClick={() => f.source_page && onJump?.(f.source_page)} data-testid={`intake-src-${f.key}`}
          className="underline decoration-dotted underline-offset-2 hover:text-[#1763B6] disabled:no-underline" disabled={!f.source_page}>
          Source: pg {f.source_page ?? '—'}{f.source_document ? ` · ${f.source_document}` : ''}
        </button>
        {f.is_handwritten && <span className="inline-flex items-center gap-0.5 text-slate-500"><PenLine size={10} /> handwritten</span>}
        {f.readability && f.readability !== 'Clear' && <span className="text-amber-600">{f.readability}</span>}
        {f.confidence != null && <span>conf {Math.round(f.confidence * 100)}%</span>}
        {f.pages_in_conflict && <span className="text-rose-500">pages disagree</span>}
      </div>
      <div className="mt-2 flex items-center justify-between">
        <label className="flex items-center gap-1.5 text-[11px] font-medium text-slate-600">
          <input type="checkbox" checked={!!f.confirmed} disabled={busy} onChange={(e) => toggle(e.target.checked)} data-testid={`intake-confirm-${f.key}`} />
          {f.confirmed ? `Confirmed${f.confirmed_by ? ` · ${f.confirmed_by}` : ''}` : 'Confirm this value'}
        </label>
        {saved && <span className="inline-flex items-center gap-0.5 text-[10px] text-emerald-600" data-testid={`intake-saved-${f.key}`}><CheckCircle2 size={11} /> Draft saved</span>}
      </div>
    </div>
  );
};

const PersonnelIntake = ({ personnelId, personnelName, defaultClassification, onClose, newHire = false }) => {
  const { token } = useContext(AuthContext);
  const [meta, setMeta] = useState(null);
  const [list, setList] = useState([]);
  const [view, setView] = useState('list'); // list | upload | review
  const [classification, setClassification] = useState(defaultClassification || 'W-2 Employee');
  const [staging, setStaging] = useState(null);
  const [busy, setBusy] = useState(false);
  const [filter, setFilter] = useState('all');
  const [pdfUrl, setPdfUrl] = useState(null);
  const [pdfPage, setPdfPage] = useState(1);
  const [saveState, setSaveState] = useState('saved'); // saved | saving | error
  const fileRef = useRef(null);
  const pollRef = useRef(null);
  const savePending = useRef(0);
  const reportSave = (s) => {
    if (s === 'saving') { savePending.current += 1; setSaveState('saving'); }
    else if (s === 'saved') { savePending.current = Math.max(0, savePending.current - 1); if (savePending.current === 0) setSaveState('saved'); }
    else { setSaveState('error'); }
  };
  const listUrl = newHire ? `${API}/personnel-intake/new-hire/list` : `${API}/personnel-intake/personnel/${personnelId}`;

  const loadList = async () => {
    const [m, l] = await Promise.all([
      axios.get(`${API}/personnel-intake/meta`, auth(token)),
      axios.get(listUrl, auth(token)),
    ]);
    setMeta(m.data); setList(l.data.items || []);
    return l.data.items || [];
  };
  useEffect(() => {
    (async () => {
      const items = await loadList();
      const resume = items.find((it) => it.status === 'extracted');
      if (resume) openReview(resume.id);
    })();
    return () => clearInterval(pollRef.current);
    /* eslint-disable-next-line */
  }, [personnelId, newHire]);

  // Load the original PDF as an authenticated blob so the browser's native
  // viewer (page navigation + zoom) can render it reliably without a token in
  // the URL. Re-fetched only when the staging record changes.
  const stagingId = staging?.id;
  useEffect(() => {
    if (view !== 'review' || !stagingId) return undefined;
    let url;
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/personnel-intake/${stagingId}/pdf`, { ...auth(token), responseType: 'blob' });
        if (cancelled) return;
        url = URL.createObjectURL(r.data);
        setPdfUrl(url);
      } catch (_) { if (!cancelled) setPdfUrl(null); }
    })();
    return () => { cancelled = true; if (url) URL.revokeObjectURL(url); setPdfUrl(null); setPdfPage(1); };
  }, [view, stagingId, token]);
  const jumpPage = (p) => { if (p) setPdfPage(Number(p)); };

  const openReview = async (sid) => {
    const r = await axios.get(`${API}/personnel-intake/${sid}`, auth(token));
    setStaging(r.data); setView('review');
    if (r.data.status === 'extracting') startPoll(sid);
  };
  const startPoll = (sid) => {
    clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const r = await axios.get(`${API}/personnel-intake/${sid}`, auth(token));
        setStaging(r.data);
        if (r.data.status !== 'extracting') { clearInterval(pollRef.current); loadList(); }
      } catch (_) { clearInterval(pollRef.current); }
    }, 5000);
  };

  const doUpload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.type !== 'application/pdf') { toast.error('Please choose a PDF packet'); return; }
    const fd = new FormData(); fd.append('file', f); fd.append('classification', classification);
    const url = newHire ? `${API}/personnel-intake/new-hire/upload` : `${API}/personnel-intake/${personnelId}/upload`;
    setBusy(true);
    try {
      const r = await axios.post(url, fd, auth(token));
      toast.success('Packet uploaded — reading with AI…');
      setStaging(r.data); setView('review'); startPoll(r.data.id); loadList();
    } catch (e2) { toast.error(e2.response?.data?.detail || 'Upload failed'); }
    setBusy(false); if (fileRef.current) fileRef.current.value = '';
  };

  const onFieldChange = (key, patch) => setStaging((s) => ({
    ...s, fields: (s.fields || []).map((x) => x.key === key ? { ...x, ...patch } : x),
  }));

  const completeReview = async () => {
    try {
      await axios.post(`${API}/personnel-intake/${staging.id}/complete`, {}, auth(token));
      setStaging((s) => ({ ...s, review_status: 'completed' }));
      toast.success('Review marked complete (staging only)');
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not complete review'); }
  };
  const reopenReview = async () => {
    try {
      await axios.post(`${API}/personnel-intake/${staging.id}/reopen`, {}, auth(token));
      setStaging((s) => ({ ...s, review_status: 'in_review' }));
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not reopen review'); }
  };
  const saveToRecord = async () => {
    const n = (staging.fields || []).filter((f) => f.confirmed).length;
    if (!window.confirm(`Save ${n} confirmed field(s) to ${personnelName}'s personnel record? Classification, pay, assignments and payroll are never changed from here.`)) return;
    setBusy(true);
    try {
      const r = await axios.post(`${API}/personnel-intake/${staging.id}/commit`, {}, auth(token));
      toast.success(`Saved ${r.data.committed.length} field(s) to the personnel record`);
      setStaging((s) => ({ ...s, review_status: 'committed' }));
      loadList();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not save to record'); }
    setBusy(false);
  };
  const createPersonnel = async () => {
    if (!window.confirm('Create a new personnel record from the confirmed fields?')) return;
    setBusy(true);
    try {
      const r = await axios.post(`${API}/personnel-intake/${staging.id}/create-personnel`, {}, auth(token));
      toast.success(`Created personnel #${r.data.personnel_number} — ${r.data.name}`);
      setStaging((s) => ({ ...s, review_status: 'committed', personnel_id: r.data.personnel_id, personnel_number: r.data.personnel_number }));
      loadList();
    } catch (e) {
      const d = e.response?.data?.detail;
      if (e.response?.status === 409 && d?.blocked) {
        setStaging((s) => ({ ...s, duplicate_candidates: d.candidates }));
        toast.error('A likely existing person was found — creation blocked.');
      } else { toast.error((typeof d === 'string' ? d : null) || 'Could not create record'); }
    }
    setBusy(false);
  };

  const counts = staging?.field_counts || {};
  const allFields = staging?.fields || [];
  const confirmedCount = allFields.filter((f) => f.confirmed).length;
  const isCompleted = staging?.review_status === 'completed';
  const isCommitted = staging?.review_status === 'committed';
  const dupes = staging?.duplicate_candidates || [];
  const fields = allFields.filter((f) => filter === 'all' || f.match_status === filter);
  const title = newHire ? 'Add Personnel — Upload Packet' : 'Upload PDF & Match Information';

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-white" data-testid="personnel-intake">
      <div className="flex items-center justify-between border-b border-slate-200 bg-[#0E2A47] px-5 py-3 text-white">
        <div className="flex items-center gap-2 text-sm font-bold">
          <FileText size={18} /> {title}
          <span className="font-normal text-slate-300">· {newHire ? (staging?.personnel_number ? `#${staging.personnel_number}` : 'New hire') : personnelName}</span>
        </div>
        <button onClick={onClose} className="rounded-lg p-1.5 hover:bg-white/10" data-testid="intake-close"><X size={18} /></button>
      </div>

      <div className="border-b border-amber-200 bg-amber-50 px-5 py-2 text-[12px] font-medium text-amber-800" data-testid="intake-staging-banner">
        <ShieldAlert size={14} className="mr-1 inline" />
        PREVIEW STAGING — reviewed values are held in an isolated staging area. Nothing is saved to {personnelName}&apos;s personnel record, payroll, classification or documents.
      </div>

      <div className="flex-1 overflow-auto">
        {/* LIST VIEW */}
        {view === 'list' && (
          <div className="mx-auto max-w-3xl p-6">
            <button onClick={() => setView('upload')} data-testid="intake-new-upload"
              className="inline-flex items-center gap-2 rounded-lg bg-[#1763B6] px-4 py-2.5 text-sm font-semibold text-white hover:bg-[#13539b]">
              <Upload size={16} /> Upload a new employment packet
            </button>
            <div className="mt-6">
              <div className="mb-2 text-xs font-bold uppercase tracking-wide text-slate-500">Previous uploads (staging)</div>
              {list.length === 0 ? <p className="text-sm text-slate-400" data-testid="intake-empty">No packets uploaded yet.</p> : (
                <div className="space-y-2">
                  {list.map((it) => (
                    <button key={it.id} onClick={() => openReview(it.id)} data-testid={`intake-row-${it.id}`}
                      className="flex w-full items-center justify-between rounded-lg border border-slate-200 bg-white p-3 text-left hover:border-[#1763B6]">
                      <div>
                        <div className="text-sm font-semibold text-[#0E2A47]">{it.original_filename}</div>
                        <div className="text-[11px] text-slate-500">{it.classification} · uploaded by {it.uploaded_by_name} · {String(it.created_at).slice(0, 10)}</div>
                      </div>
                      <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${it.status === 'extracted' ? 'bg-emerald-50 text-emerald-700' : it.status === 'failed' ? 'bg-rose-50 text-rose-700' : 'bg-blue-50 text-blue-700'}`}>{it.status}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* UPLOAD VIEW */}
        {view === 'upload' && (
          <div className="mx-auto max-w-lg p-6">
            <button onClick={() => setView('list')} className="mb-4 inline-flex items-center gap-1 text-sm text-[#1763B6]" data-testid="intake-back-list"><ChevronLeft size={16} /> Back</button>
            <div className="rounded-xl border border-slate-200 bg-white p-5">
              <label className="block text-xs font-bold uppercase text-slate-500">Worker classification (confirm before extraction)</label>
              <select value={classification} onChange={(e) => setClassification(e.target.value)} data-testid="intake-classification"
                className="mt-1 w-full rounded border border-slate-300 px-3 py-2 text-sm">
                {(meta?.classifications || []).map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
              <p className="mt-1 text-[11px] text-slate-500">This guides interpretation only. It will never change the existing approved classification.</p>
              <div className="mt-5">
                <input ref={fileRef} type="file" accept="application/pdf" onChange={doUpload} className="hidden" data-testid="intake-file-input" />
                <button disabled={busy} onClick={() => fileRef.current?.click()} data-testid="intake-choose-file"
                  className="inline-flex w-full items-center justify-center gap-2 rounded-lg border-2 border-dashed border-slate-300 bg-slate-50 px-4 py-8 text-sm font-semibold text-slate-600 hover:border-[#1763B6] hover:text-[#1763B6] disabled:opacity-60">
                  {busy ? <><Loader2 size={18} className="animate-spin" /> Uploading…</> : <><Upload size={18} /> Choose scanned PDF packet</>}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* REVIEW VIEW */}
        {view === 'review' && staging && (
          <div className="flex h-full flex-col lg:flex-row">
            <div className="h-[42vh] shrink-0 border-b border-slate-200 bg-slate-100 lg:h-full lg:w-1/2 lg:border-b-0 lg:border-r">
              <PdfViewer fileUrl={pdfUrl} page={pdfPage} setPage={setPdfPage} />
            </div>
            <div className="flex-1 overflow-auto p-4 lg:w-1/2">
              <button onClick={() => { clearInterval(pollRef.current); setView('list'); loadList(); }} className="mb-3 inline-flex items-center gap-1 text-sm text-[#1763B6]" data-testid="intake-back-review"><ChevronLeft size={16} /> Back to uploads</button>

              {staging.status === 'extracting' && (
                <div className="flex flex-col items-center justify-center py-16 text-slate-500" data-testid="intake-extracting">
                  <Loader2 size={28} className="animate-spin text-[#1763B6]" />
                  <p className="mt-3 text-sm font-medium">Reading the packet with AI…</p>
                  <p className="text-[11px] text-slate-400">Handwriting + multi-document extraction can take up to ~2 minutes.</p>
                </div>
              )}
              {staging.status === 'failed' && (
                <div className="rounded-lg border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700" data-testid="intake-failed">
                  Extraction failed: {staging.error || 'unknown error'}
                  <button onClick={async () => { await axios.post(`${API}/personnel-intake/${staging.id}/re-extract`, {}, auth(token)); setStaging((s) => ({ ...s, status: 'extracting' })); startPoll(staging.id); }}
                    className="ml-2 inline-flex items-center gap-1 rounded bg-rose-600 px-2 py-1 text-[11px] font-semibold text-white" data-testid="intake-retry"><RefreshCw size={12} /> Retry</button>
                </div>
              )}

              {staging.status === 'extracted' && (
                <>
                  <div className="mb-3 flex flex-wrap items-center justify-between gap-2 rounded-lg border border-slate-200 bg-slate-50 p-3" data-testid="intake-review-bar">
                    <div className="flex items-center gap-3 text-[12px] text-slate-600">
                      <span className="font-semibold text-[#0E2A47]" data-testid="intake-progress">{confirmedCount} / {allFields.length} confirmed</span>
                      <span className={`inline-flex items-center gap-1 ${saveState === 'error' ? 'text-rose-600' : saveState === 'saving' ? 'text-amber-600' : 'text-emerald-600'}`} data-testid="intake-save-light">
                        {saveState === 'saving' ? <><Loader2 size={12} className="animate-spin" /> Saving…</>
                          : saveState === 'error' ? <><AlertTriangle size={12} /> Save failed — retry</>
                          : <><CheckCircle2 size={12} /> All changes saved</>}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      {!isCompleted && !isCommitted && (
                        <button onClick={completeReview} data-testid="intake-complete"
                          className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-[12px] font-semibold text-slate-700 hover:bg-slate-100">
                          <CheckCircle2 size={14} /> Mark review complete
                        </button>
                      )}
                      {isCompleted && (
                        <span className="inline-flex items-center gap-2">
                          <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2.5 py-1 text-[11px] font-semibold text-emerald-700" data-testid="intake-completed-badge"><CheckCircle2 size={13} /> Review complete</span>
                          <button onClick={reopenReview} className="text-[11px] font-semibold text-[#1763B6] underline" data-testid="intake-reopen">Reopen</button>
                        </span>
                      )}
                      {isCommitted ? (
                        <span className="inline-flex items-center gap-1 rounded-full bg-emerald-600 px-2.5 py-1 text-[11px] font-semibold text-white" data-testid="intake-committed-badge">
                          <CheckCircle2 size={13} /> {newHire ? `Created #${staging.personnel_number}` : 'Saved to record'}
                        </span>
                      ) : newHire ? (
                        <button onClick={createPersonnel} disabled={busy || confirmedCount === 0} data-testid="intake-create-personnel"
                          className="inline-flex items-center gap-1.5 rounded-lg bg-[#1763B6] px-3 py-1.5 text-[12px] font-semibold text-white hover:bg-[#12518f] disabled:opacity-50">
                          <CheckCircle2 size={14} /> Create personnel record
                        </button>
                      ) : (
                        <button onClick={saveToRecord} disabled={busy || confirmedCount === 0} data-testid="intake-save-record"
                          className="inline-flex items-center gap-1.5 rounded-lg bg-[#1763B6] px-3 py-1.5 text-[12px] font-semibold text-white hover:bg-[#12518f] disabled:opacity-50">
                          <CheckCircle2 size={14} /> Save confirmed to record
                        </button>
                      )}
                    </div>
                  </div>
                  {staging.is_stale && !isCommitted && (
                    <div className="mb-3 rounded-lg border border-amber-200 bg-amber-50 p-3 text-[12px] text-amber-800" data-testid="intake-stale">
                      <Clock size={14} className="mr-1 inline" /><b>This draft has been open {staging.age_days} days.</b> Please finish confirming and save to the record (or mark the review complete) so it doesn't stall.
                    </div>
                  )}
                  {newHire && dupes.length > 0 && (
                    <div className="mb-3 rounded-lg border border-rose-200 bg-rose-50 p-3 text-[12px] text-rose-700" data-testid="intake-duplicates">
                      <ShieldAlert size={14} className="mr-1 inline" /><b>Possible existing person — creation blocked.</b>{' '}
                      {dupes.map((c) => `${c.name} (#${c.personnel_number}: ${c.reasons.join(', ')})`).join('; ')}. Open that record and use Upload PDF &amp; Match instead.
                    </div>
                  )}
                  {(staging.expired_documents || []).length > 0 && (
                    <div className="mb-3 rounded-lg border border-rose-200 bg-rose-50 p-3 text-[12px] text-rose-700" data-testid="intake-expired">
                      <FileWarning size={14} className="mr-1 inline" /><b>Expired documents flagged:</b>{' '}
                      {staging.expired_documents.map((e) => `${e.document} (exp ${e.expiration_date})`).join(', ')}
                    </div>
                  )}

                  <div className="mb-3 flex flex-wrap gap-1.5" data-testid="intake-filters">
                    <button onClick={() => setFilter('all')} className={`rounded-full border px-2.5 py-1 text-[11px] font-semibold ${filter === 'all' ? 'border-[#0E2A47] bg-[#0E2A47] text-white' : 'border-slate-200 text-slate-600'}`}>All {(staging.fields || []).length}</button>
                    {ORDER.map((k) => counts[k] ? (
                      <button key={k} onClick={() => setFilter(k)} data-testid={`intake-filter-${k}`}
                        className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-[11px] font-semibold ${filter === k ? STATUS_META[k].tone : 'border-slate-200 text-slate-600'}`}>
                        <span className={`h-1.5 w-1.5 rounded-full ${STATUS_META[k].dot}`} />{STATUS_META[k].label} {counts[k]}
                      </button>
                    ) : null)}
                  </div>

                  <div className="space-y-3">
                    {fields.map((f) => <FieldRow key={f.key} f={f} sid={staging.id} token={token} onChange={onFieldChange} onJump={jumpPage} reportSave={reportSave} />)}
                  </div>

                  <div className="mt-4 rounded-lg border border-slate-200 bg-slate-50 p-3 text-[11px] text-slate-500" data-testid="intake-save-note">
                    <CheckCircle2 size={13} className="mr-1 inline text-slate-400" />
                    Saving confirmed values to the personnel record is intentionally disabled in this proof of concept. Historical signatures and dates are recorded as historical facts only — never as new digital signatures.
                  </div>
                  {(staging.blank_or_incomplete || []).length > 0 && (
                    <div className="mt-3 rounded-lg border border-slate-200 bg-white p-3 text-[11px] text-slate-500">
                      <div className="mb-1 flex items-center gap-1 font-semibold text-slate-600"><AlertTriangle size={12} /> Blank / incomplete noted</div>
                      <ul className="list-disc pl-4">{staging.blank_or_incomplete.map((b, i) => <li key={i}>{b}</li>)}</ul>
                    </div>
                  )}
                  <div className="mt-3 text-[10px] text-slate-400"><Clock size={10} className="mr-1 inline" />Uploaded by {staging.uploaded_by_name} · {staging.documents?.length || 0} documents detected</div>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PersonnelIntake;
